import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useToolResult } from '../contexts/ToolResultContext';
import TalkingAvatar from './TalkingAvatar';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import {
  Send, Loader, Plus, MessageSquare, Trash2, X,
  Wrench, ChevronRight, Clock, Brain, Mic, MicOff,
  Volume2, VolumeX, Minimize2, Maximize2, ThumbsUp, ThumbsDown,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api/compass/agent`;

// ─── Tool context mapping ───
const TOOL_CONTEXT = {
  '/portal/ai-tools/sales':      { nl: 'AI Sales Assistant',       en: 'AI Sales Assistant' },
  '/portal/ai-tools/knowledge':  { nl: 'AI Knowledge Brain',       en: 'AI Knowledge Brain' },
  '/portal/ai-tools/pulse':      { nl: 'AI Customer Pulse',        en: 'AI Customer Pulse' },
  '/portal/ai-tools/radar':      { nl: 'AI Opportunity Radar',     en: 'AI Opportunity Radar' },
  '/portal/ai-tools/risk':       { nl: 'AI Risk Detector',         en: 'AI Risk Detector' },
  '/portal/ai-tools/pricing':    { nl: 'AI Pricing Optimizer',     en: 'AI Pricing Optimizer' },
  '/portal/ai-tools/mapper':     { nl: 'AI Process Mapper',        en: 'AI Process Mapper' },
  '/portal/ai-tools/meeting':    { nl: 'AI Meeting Brain',         en: 'AI Meeting Brain' },
  '/portal/ai-tools/coach':      { nl: 'AI Coach',                 en: 'AI Coach' },
  '/portal/ai-tools/explorer':   { nl: 'AI Insight Explorer',      en: 'AI Insight Explorer' },
  '/portal/ai-tools/automation': { nl: 'AI Automation Assistant',   en: 'AI Automation Assistant' },
  '/portal/ai-tools/cashflow':   { nl: 'AI Cashflow Predictor',    en: 'AI Cashflow Predictor' },
  '/portal/ai-tools/hiring':     { nl: 'AI Hiring Assistant',      en: 'AI Hiring Assistant' },
  '/portal/ai-tools/simulator':  { nl: 'AI Decision Simulator',    en: 'AI Decision Simulator' },
  '/portal/ai-tools/health-check':       { nl: 'AI Business Health Check', en: 'AI Business Health Check' },
  '/portal/ai-tools/financial-dashboard': { nl: 'AI Financial Dashboard',   en: 'AI Financial Dashboard' },
  '/portal/ai-tools/smart-alerts':        { nl: 'AI Smart Alerts',          en: 'AI Smart Alerts' },
  '/portal/analytics':           { nl: 'Analytics Dashboard',      en: 'Analytics Dashboard' },
  '/portal/reports':             { nl: 'Rapporten',                en: 'Reports' },
  '/portal/company-profile':     { nl: 'Bedrijfsprofiel',          en: 'Company Profile' },
  '/portal/accounting':          { nl: 'Boekhoudsoftware Koppelingen', en: 'Accounting Integrations' },
};

const CONTEXT_SUGGESTIONS = {
  nl: {
    '/portal/ai-tools/sales':      ['Hoe verhoog ik mijn conversieratio?', 'Analyseer mijn verkoopdata', 'Schrijf een sales e-mail voor mijn product'],
    '/portal/ai-tools/knowledge':  ['Hoe werkt de Knowledge Brain?', 'Welke documenten kan ik uploaden?', 'Zoek informatie over ons beleid'],
    '/portal/ai-tools/pulse':      ['Wat zijn de trends in klantfeedback?', 'Hoe meet ik klanttevredenheid?', 'Analyseer onze reviews'],
    '/portal/ai-tools/cashflow':   ['Voorspel mijn cashflow voor de komende 3 maanden', 'Waar kan ik kosten besparen?', 'Wat zijn de risicos voor onze liquiditeit?'],
    '/portal/ai-tools/pricing':    ['Hoe optimaliseer ik mijn prijzen?', 'Wat doen concurrenten met hun prijzen?', 'Bereken de optimale marge'],
    '/portal/ai-tools/risk':       ['Welke risicos heeft mijn bedrijf?', 'Hoe minimaliseer ik operationele risicos?', 'Analyseer financiele risicos'],
    '/portal/ai-tools/mapper':     ['Breng mijn bedrijfsprocessen in kaart', 'Waar zitten knelpunten in onze workflow?', 'Hoe kan ik dit proces verbeteren?'],
    '/portal/ai-tools/meeting':    ['Vat mijn vergadernotities samen', 'Maak actiepunten van deze meeting', 'Wat zijn de belangrijkste besluiten?'],
    '/portal/ai-tools/coach':      ['Geef me tips voor beter leiderschap', 'Hoe motiveer ik mijn team?', 'Help me met mijn persoonlijke ontwikkeling'],
    '/portal/ai-tools/explorer':   ['Welke patronen zie je in mijn data?', 'Analyseer mijn CSV-bestand', 'Wat zijn de belangrijkste inzichten?'],
    '/portal/ai-tools/automation': ['Welke taken kan ik automatiseren?', 'Wat is de ROI van automatisering?', 'Identificeer repetitief werk'],
    '/portal/ai-tools/hiring':     ['Analyseer deze CV voor een vacature', 'Vergelijk twee kandidaten', 'Schrijf een functieprofiel'],
    '/portal/ai-tools/simulator':  ['Simuleer uitbreiding naar een nieuwe markt', 'Wat als ik 2 medewerkers aanneem?', 'Vergelijk twee strategische opties'],
    '/portal/ai-tools/radar':      ['Welke groeikansen mis ik?', 'Analyseer markttrends in mijn sector', 'Waar liggen onbenutte kansen?'],
    '/portal/ai-tools/health-check':       ['Hoe gezond is mijn bedrijf?', 'Waar scoor ik laag?', 'Geef me verbeterpunten'],
    '/portal/ai-tools/financial-dashboard': ['Hoe staan mijn financiele KPIs?', 'Vergelijk met benchmarks', 'Waar kan ik optimaliseren?'],
    '/portal/ai-tools/smart-alerts':        ['Welke waarschuwingen zijn belangrijk?', 'Wat moet ik nu actie op nemen?', 'Stel alerts in voor cashflow'],
    '/portal/accounting':                    ['Hoe koppel ik mijn boekhoudsoftware?', 'Synchroniseer mijn boekhouddata', 'Toon mijn financiele overzicht'],
  },
  en: {
    '/portal/ai-tools/sales':      ['How can I increase my conversion rate?', 'Analyze my sales data', 'Draft a sales email for my product'],
    '/portal/ai-tools/knowledge':  ['How does the Knowledge Brain work?', 'What documents can I upload?', 'Search for info on our policies'],
    '/portal/ai-tools/pulse':      ['What are the trends in customer feedback?', 'How do I measure satisfaction?', 'Analyze our reviews'],
    '/portal/ai-tools/cashflow':   ['Predict my cashflow for the next 3 months', 'Where can I cut costs?', 'What are the liquidity risks?'],
    '/portal/ai-tools/pricing':    ['How do I optimize my pricing?', 'What are competitors charging?', 'Calculate the optimal margin'],
    '/portal/ai-tools/risk':       ['What risks does my business face?', 'How do I minimize operational risk?', 'Analyze financial risks'],
    '/portal/ai-tools/mapper':     ['Map my business processes', 'Where are the bottlenecks?', 'How can I improve this process?'],
    '/portal/ai-tools/meeting':    ['Summarize my meeting notes', 'Create action items from this meeting', 'What were the key decisions?'],
    '/portal/ai-tools/coach':      ['Give me leadership tips', 'How do I motivate my team?', 'Help with my personal development'],
    '/portal/ai-tools/explorer':   ['What patterns do you see in my data?', 'Analyze my CSV file', 'What are the key insights?'],
    '/portal/ai-tools/automation': ['What tasks can I automate?', 'What is the ROI of automation?', 'Identify repetitive work'],
    '/portal/ai-tools/hiring':     ['Analyze this CV for a vacancy', 'Compare two candidates', 'Write a job profile'],
    '/portal/ai-tools/simulator':  ['Simulate expansion into a new market', 'What if I hire 2 more employees?', 'Compare two strategic options'],
    '/portal/ai-tools/radar':      ['What growth opportunities am I missing?', 'Analyze market trends in my sector', 'Where are untapped opportunities?'],
    '/portal/ai-tools/health-check':       ['How healthy is my business?', 'Where do I score low?', 'Give me improvement suggestions'],
    '/portal/ai-tools/financial-dashboard': ['How are my financial KPIs?', 'Compare with benchmarks', 'Where can I optimize?'],
    '/portal/ai-tools/smart-alerts':        ['Which alerts are important?', 'What should I act on now?', 'Set alerts for cashflow'],
    '/portal/accounting':                    ['How do I connect my accounting software?', 'Sync my accounting data', 'Show my financial overview'],
  },
};

const stripMarkdown = (md) =>
  ((md) == null ? '' : String(md)).replace(/#{1,6}\s?/g, '').replace(/\*\*|__/g, '').replace(/\*|_/g, '')
    .replace(/`{1,3}[^`]*`{1,3}/g, '').replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/^\s*[-*+]\s/gm, '').replace(/^\s*\d+\.\s/gm, '')
    .replace(/\n{2,}/g, '. ').replace(/\n/g, ' ').trim();

const SpeechRecognition = typeof window !== 'undefined'
  ? (window.SpeechRecognition || window.webkitSpeechRecognition) : null;

const TOOL_LABELS = {
  cashflow_analyse: { label: 'Cashflow Analyse' },
  beslissing_simuleren: { label: 'Beslissing Simuleren' },
  cv_analyseren: { label: 'CV Analyseren' },
  kandidaten_vergelijken: { label: 'Kandidaten Vergelijken' },
  geheugen_opslaan: { label: 'Geheugen Opslaan' },
  geheugen_ophalen: { label: 'Geheugen Ophalen' },
};

const EmmaSidebar = ({ onOpenChange }) => {
  const { token, user } = useAuth();
  const { lang } = useLanguage();
  const { toolResult } = useToolResult();
  const location = useLocation();
  const navigate = useNavigate();
  const chatEndRef = useRef(null);
  const inputRef = useRef(null);

  const [isOpen, setIsOpen] = useState(false);

  // Notify parent when open state changes
  useEffect(() => {
    onOpenChange?.(isOpen);
  }, [isOpen, onOpenChange]);
  const [showHistory, setShowHistory] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [memories, setMemories] = useState([]);

  // Voice
  const [isListening, setIsListening] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const recognitionRef = useRef(null);
  const ttsAbortRef = useRef(false);
  const ttsTimerRef = useRef(null);
  const avatarRef = useRef(null);

  // Context awareness
  const currentPath = location.pathname;
  const toolCtx = TOOL_CONTEXT[currentPath];
  const toolName = toolCtx ? toolCtx[lang] || toolCtx.en : null;
  const contextSuggestions = CONTEXT_SUGGESTIONS[lang]?.[currentPath];

  const headers = { Authorization: `Bearer ${token}` };

  const txt = lang === 'nl' ? {
    placeholder: toolName ? `Vraag over ${toolName}...` : 'Stel Emma een vraag...',
    newChat: 'Nieuw gesprek',
    sessions: 'Gesprekken',
    noSessions: 'Nog geen gesprekken — begin er een om te starten.',
    thinking: 'Denken...',
    welcome: toolName ? `Ik help je met ${toolName}` : 'Hoi! Ik ben Emma',
    welcomeDesc: toolName
      ? `Ik zie dat je werkt met ${toolName}. Stel me een vraag over deze tool of beschrijf je uitdaging.`
      : 'Beschrijf je uitdaging en ik help je de juiste tool te vinden.',
    listening: 'Luisteren...',
    speaking: 'Spreken... (klik om te stoppen)',
    memory: 'Geheugen',
    helpful: 'Dit hielp',
    notHelpful: 'Dit hielp niet',
    thanksFeedback: 'Dank je — Emma leert hiervan.',
    suggestions: contextSuggestions || [
      'Welke tool past bij klantfeedback analyse?',
      'Ik wil mijn cashflow voorspellen',
      'Help me met mijn prijsstrategie',
    ],
  } : {
    placeholder: toolName ? `Ask about ${toolName}...` : 'Ask Emma a question...',
    newChat: 'New chat',
    sessions: 'Conversations',
    noSessions: 'No conversations yet — start one to begin.',
    thinking: 'Thinking...',
    welcome: toolName ? `I'll help with ${toolName}` : 'Hi! I\'m Emma',
    welcomeDesc: toolName
      ? `I see you're working with ${toolName}. Ask me anything about this tool or describe your challenge.`
      : 'Describe your challenge and I\'ll help you find the right tool.',
    listening: 'Listening...',
    speaking: 'Speaking... (click to stop)',
    memory: 'Memory',
    helpful: 'This helped',
    notHelpful: 'This did not help',
    thanksFeedback: 'Thanks — Emma learns from this.',
    suggestions: contextSuggestions || [
      'Which tool fits customer feedback analysis?',
      'I want to predict my cashflow',
      'Help me with my pricing strategy',
    ],
  };

  useEffect(() => {
    if (isOpen && token) { fetchSessions(); fetchMemories(); }
  }, [isOpen, token]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  useEffect(() => {
    return () => {
      ttsAbortRef.current = true;
      if (ttsTimerRef.current) clearTimeout(ttsTimerRef.current);
      if (recognitionRef.current) recognitionRef.current.abort();
      if (window._emmaTTSAudio) { window._emmaTTSAudio.pause(); window._emmaTTSAudio = null; }
    };
  }, []);

  const fetchSessions = async () => {
    try {
      const res = await axios.get(`${API}/sessions`, { headers });
      setSessions(res.data.sessions || []);
    } catch { /* silent */ }
  };

  const fetchMemories = async () => {
    try {
      const res = await axios.get(`${API}/memory`, { headers });
      setMemories(res.data.memories || []);
    } catch { /* silent */ }
  };

  const loadSession = async (sid) => {
    try {
      const res = await axios.get(`${API}/session/${sid}`, { headers });
      setMessages(res.data.messages || []);
      setSessionId(sid);
      setShowHistory(false);
    } catch { /* silent */ }
  };

  const deleteSession = async (sid) => {
    try {
      await axios.delete(`${API}/session/${sid}`, { headers });
      setSessions(prev => (Array.isArray(prev) ? prev : []).filter(s => s.session_id !== sid));
      if (sessionId === sid) startNew();
    } catch { /* silent */ }
  };

  // TTS with avatar lip-sync
  const speakText = useCallback(async (text) => {
    if (!voiceEnabled) return;
    const clean = stripMarkdown(text);
    if (!clean) return;

    // Protect dots inside Dutch numbers (thousands separator) before sentence splitting.
    // Replace digit.digit with a placeholder, split sentences, then restore.
    const protected_ = ((clean) == null ? '' : String(clean)).replace(/(\d)\.(\d)/g, '$1\u2024$2');
    const rawSentences = protected_.match(/[^.!?;:]+[.!?;:]?/g) || [protected_];
    const sentences = (Array.isArray(rawSentences) ? rawSentences : []).map(s => ((s) == null ? '' : String(s)).replace(/\u2024/g, '.'));
    const chunks = [];
    let current = '';
    for (const s of sentences) {
      current += s;
      if (current.length > 60) { chunks.push(((current) == null ? '' : String(current)).trim()); current = ''; }
    }
    if (((current) == null ? '' : String(current)).trim()) chunks.push(((current) == null ? '' : String(current)).trim());
    if (chunks.length === 0) return;

    setIsSpeaking(true);
    ttsAbortRef.current = false;

    const playChunk = async (idx) => {
      if (ttsAbortRef.current || idx >= chunks.length) { setIsSpeaking(false); return; }
      try {
        // Use speak-sync to get word-level timing for lip-sync
        const res = await fetch(`${BACKEND_URL}/api/tts/speak-sync`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ text: chunks[idx], lang }),
        });
        if (!res.ok) throw new Error();
        const data = await res.json();

        // Convert base64 audio to blob
        const audioBytes = Uint8Array.from(atob(data.audio), c => c.charCodeAt(0));
        const blob = new Blob([audioBytes], { type: 'audio/mpeg' });

        // Build sync data object with word timing from Edge-TTS
        const syncData = (data.words?.length > 0)
          ? { words: data.words, wtimes: data.wtimes, wdurations: data.wdurations }
          : chunks[idx]; // fallback to text string

        // Try avatar lip-sync first
        if (avatarRef.current?.isReady?.()) {
          const spoke = await avatarRef.current.speakAudio(blob, syncData);
          if (spoke) {
            // Calculate actual audio duration for next chunk scheduling
            const totalDuration = (data.wtimes?.length > 0)
              ? data.wtimes[data.wtimes.length - 1] + data.wdurations[data.wdurations.length - 1]
              : 3000;
            ttsTimerRef.current = setTimeout(() => {
              if (!ttsAbortRef.current) playChunk(idx + 1);
            }, totalDuration + 300);
            return;
          }
        }

        // Fallback: regular audio playback
        const url = URL.createObjectURL(blob);
        const audio = new Audio(url);
        audio.onended = () => { URL.revokeObjectURL(url); if (!ttsAbortRef.current) playChunk(idx + 1); };
        audio.onerror = () => { URL.revokeObjectURL(url); if (!ttsAbortRef.current) playChunk(idx + 1); };
        audio.play();
        window._emmaTTSAudio = audio;
      } catch {
        if (!ttsAbortRef.current) playChunk(idx + 1);
      }
    };
    playChunk(0);
  }, [voiceEnabled, lang]);

  const stopSpeaking = () => {
    ttsAbortRef.current = true;
    if (ttsTimerRef.current) { clearTimeout(ttsTimerRef.current); ttsTimerRef.current = null; }
    if (window._emmaTTSAudio) { window._emmaTTSAudio.pause(); window._emmaTTSAudio = null; }
    avatarRef.current?.stopSpeaking?.();
    setIsSpeaking(false);
  };

  const startListening = () => {
    if (!SpeechRecognition) return;
    if (isListening) { recognitionRef.current?.stop(); setIsListening(false); return; }
    const recognition = new SpeechRecognition();
    recognition.lang = lang === 'nl' ? 'nl-NL' : 'en-US';
    recognition.interimResults = true;
    recognition.continuous = false;
    recognition.onstart = () => setIsListening(true);
    recognition.onresult = (event) => {
      const results = Array.from(event.results);
      const transcript = (Array.isArray(results) ? results : []).map(r => r[0].transcript).join('');
      setInput(transcript);
      if ((Array.isArray(results) ? results : []).some(r => r.isFinal) && ((transcript) == null ? '' : String(transcript)).trim()) {
        setIsListening(false);
        setTimeout(() => sendMessageDirect(((transcript) == null ? '' : String(transcript)).trim()), 200);
      }
    };
    recognition.onerror = () => setIsListening(false);
    recognition.onend = () => setIsListening(false);
    recognitionRef.current = recognition;
    recognition.start();
  };

  // Rate one of Emma's answers. This is her only real outcome signal — without it she
  // has to guess from her own wording whether she helped, which she always gets wrong
  // in her own favour.
  const rateMessage = async (index, messageId, rating) => {
    if (!messageId) return;
    const previous = messages[index]?.user_feedback;
    const next = previous === rating ? null : rating;   // clicking the same thumb undoes it
    if (!next) return;                                   // nothing to send for an un-rate
    setMessages(prev => (Array.isArray(prev) ? prev : []).map((m, i) =>
      i === index ? { ...m, user_feedback: next } : m));
    try {
      await axios.post(`${API}/feedback`, { message_id: messageId, rating: next }, { headers });
    } catch {
      // Put the old state back — never show a rating we failed to store.
      setMessages(prev => (Array.isArray(prev) ? prev : []).map((m, i) =>
        i === index ? { ...m, user_feedback: previous } : m));
    }
  };

  const sendMessageDirect = async (text) => {
    if (!text?.trim() || loading) return;
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: text }]);
    setLoading(true);
    try {
      const res = await axios.post(`${API}/chat`, {
        message: text, session_id: sessionId, language: lang,
        tool_context: toolName || null,
        tool_result: toolResult?.summary || null,
      }, { headers });
      const { session_id, message_id, response, tools_used, actions } = res.data;
      setSessionId(session_id);

      // Check for navigation commands in response (legacy) or actions (new)
      const navMatch = response.match(/\[NAVIGATE:(\/[^\]]+)\]/);
      const cleanResponse = ((response) == null ? '' : String(response)).replace(/\[NAVIGATE:\/[^\]]+\]/g, '').trim();

      setMessages(prev => [...prev, { role: 'assistant', content: cleanResponse, tools_used: tools_used || [], message_id }]);
      speakText(cleanResponse);

      // Execute navigation from actions array (OpenClaw agentic)
      if (actions?.length > 0) {
        for (const action of actions) {
          if (action.type === 'navigate' && action.route) {
            setTimeout(() => navigate(action.route), 800);
          }
        }
      } else if (navMatch) {
        // Legacy fallback
        setTimeout(() => navigate(navMatch[1]), 800);
      }

      fetchSessions();
      if (tools_used?.some(t => t.tool === 'geheugen_opslaan')) fetchMemories();
    } catch (err) {
      const errMsg = lang === 'en'
        ? `Error: ${err.response?.data?.detail || 'Request failed. Please check your API configuration in Admin Console.'}`
        : `Fout: ${err.response?.data?.detail || 'Verzoek mislukt. Controleer je API-configuratie in de Admin Console.'}`;
      setMessages(prev => [...prev, { role: 'assistant', content: errMsg }]);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const sendMessage = (msg) => sendMessageDirect(msg || input);

  const startNew = () => {
    setSessionId(null);
    setMessages([]);
    setIsSpeaking(false);
    stopSpeaking();
  };

  // ─── Collapsed: floating button ───
  if (!isOpen) {
    return (
      <div className="emma-fab-wrapper" data-testid="emma-fab" onClick={() => setIsOpen(true)}>
        <div className="emma-fab-avatar">
          <TalkingAvatar ref={null} lang={lang} visible={true} showNameBadge={false} cameraView="head" />
        </div>
        <span className="emma-fab-name">Emma</span>
      </div>
    );
  }

  // ─── Expanded: sidebar panel ───
  return (
    <aside className="emma-sidebar" data-testid="emma-sidebar">
      {/* Header */}
      <div className="emma-header">
        <div className="emma-header-left">
          <div className="emma-avatar-dot" />
          <div>
            <span className="emma-header-title">Emma</span>
            <span className="emma-context-badge" data-testid="emma-context">{toolName || (lang === 'nl' ? 'Jouw AI Gids' : 'Your AI Guide')}</span>
          </div>
        </div>
        <div className="emma-header-actions">
          <button onClick={() => setVoiceEnabled(!voiceEnabled)} className="emma-icon-btn" data-testid="emma-voice-toggle"
            title={voiceEnabled ? 'Stem uit' : 'Stem aan'}>
            {voiceEnabled ? <Volume2 size={14} /> : <VolumeX size={14} />}
          </button>
          <button onClick={() => setShowHistory(!showHistory)} className="emma-icon-btn" data-testid="emma-history-toggle"
            title={txt.sessions}>
            <Clock size={14} />
          </button>
          <button onClick={startNew} className="emma-icon-btn" data-testid="emma-new-chat" title={txt.newChat}>
            <Plus size={14} />
          </button>
          <button onClick={() => setIsOpen(false)} className="emma-icon-btn" data-testid="emma-close">
            <Minimize2 size={14} />
          </button>
        </div>
      </div>

      {/* History dropdown */}
      {showHistory && (
        <div className="emma-history-panel" data-testid="emma-history-panel">
          <div className="emma-history-header">
            <span className="emma-history-title">{txt.sessions}</span>
            <span className="emma-memory-badge">{txt.memory}: {memories.length}</span>
          </div>
          <div className="emma-history-list">
            {sessions.length === 0 && <span className="emma-empty">{txt.noSessions}</span>}
            {(Array.isArray(sessions) ? sessions : []).map(s => (
              <div key={s.session_id} className={`emma-history-item ${sessionId === s.session_id ? 'active' : ''}`}
                onClick={() => loadSession(s.session_id)} data-testid={`emma-session-${s.session_id}`}>
                <span className="emma-history-text">{s.preview || 'Gesprek'}</span>
                <button onClick={(e) => { e.stopPropagation(); deleteSession(s.session_id); }} className="emma-icon-btn-sm">
                  <Trash2 size={11} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Messages */}
      <div className="emma-messages" data-testid="emma-messages">
        {messages.length === 0 && !loading && (
          <div className="emma-welcome">
            <div className="emma-welcome-icon">
              <MessageSquare size={22} />
            </div>
            <h4 className="emma-welcome-title">{txt.welcome}</h4>
            <p className="emma-welcome-desc">{txt.welcomeDesc}</p>
            <div className="emma-suggestions">
              {(Array.isArray(txt.suggestions) ? txt.suggestions : []).map((s, i) => (
                <button key={i} className="emma-suggestion" onClick={() => sendMessage(s)} data-testid={`emma-suggestion-${i}`}>
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {(Array.isArray(messages) ? messages : []).map((msg, i) => (
          <div key={i} className={`emma-msg emma-msg-${msg.role}`}>
            {msg.role === 'assistant' ? (
              <div>
                {msg.tools_used?.length > 0 && (
                  <div className="emma-tool-badges">
                    {(Array.isArray(msg.tools_used) ? msg.tools_used : []).map((t, ti) => (
                      <span key={ti} className="emma-tool-badge" data-testid={`emma-tool-${t.tool}`}>
                        <Wrench size={9} /> {TOOL_LABELS[t.tool]?.label || t.tool}
                      </span>
                    ))}
                  </div>
                )}
                <div className="emma-msg-content"><ReactMarkdown>{msg.content}</ReactMarkdown></div>
                <div className="emma-msg-actions">
                  {voiceEnabled && (
                    <button onClick={() => speakText(msg.content)} className="emma-speak-btn" data-testid={`emma-speak-${i}`}>
                      <Volume2 size={12} />
                    </button>
                  )}
                  {msg.message_id && (
                    <>
                      <button
                        onClick={() => rateMessage(i, msg.message_id, 'up')}
                        className={`emma-rate-btn ${msg.user_feedback === 'up' ? 'active' : ''}`}
                        title={txt.helpful} aria-label={txt.helpful}
                        data-testid={`emma-rate-up-${i}`}>
                        <ThumbsUp size={12} />
                      </button>
                      <button
                        onClick={() => rateMessage(i, msg.message_id, 'down')}
                        className={`emma-rate-btn ${msg.user_feedback === 'down' ? 'active' : ''}`}
                        title={txt.notHelpful} aria-label={txt.notHelpful}
                        data-testid={`emma-rate-down-${i}`}>
                        <ThumbsDown size={12} />
                      </button>
                      {msg.user_feedback && (
                        <span className="emma-rate-thanks">{txt.thanksFeedback}</span>
                      )}
                    </>
                  )}
                </div>
              </div>
            ) : (
              <div className="emma-msg-content">{msg.content}</div>
            )}
          </div>
        ))}

        {loading && (
          <div className="emma-msg emma-msg-assistant">
            <div className="emma-msg-content emma-thinking">
              <Loader className="spin" size={14} /> {txt.thinking}
            </div>
          </div>
        )}
        <div ref={chatEndRef} />
      </div>

      {/* 3D Avatar */}
      <div className="emma-avatar-container" data-testid="emma-avatar-container">
        <TalkingAvatar ref={avatarRef} lang={lang} visible={isOpen} showNameBadge={false} cameraView="head" background="linear-gradient(180deg, #e8eaf6 0%, #c5cae9 100%)" />
      </div>

      {/* Input */}
      <form className="emma-input-bar" onSubmit={(e) => { e.preventDefault(); sendMessage(); }} data-testid="emma-form">
        {isListening && (
          <div className="emma-listening-badge" data-testid="emma-listening">
            <Mic size={12} /> {txt.listening}
          </div>
        )}
        {isSpeaking && (
          <button type="button" onClick={stopSpeaking} className="emma-speaking-badge" data-testid="emma-speaking">
            <Volume2 size={12} /> {txt.speaking}
          </button>
        )}
        <button type="button" onClick={startListening} disabled={loading}
          className={`emma-mic-btn ${isListening ? 'active' : ''}`} data-testid="emma-mic">
          {isListening ? <MicOff size={16} /> : <Mic size={16} />}
        </button>
        <input
          ref={inputRef}
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={txt.placeholder}
          disabled={loading}
          className="emma-input"
          data-testid="emma-input"
        />
        <button type="submit" disabled={loading || !((input) == null ? '' : String(input)).trim()} className="emma-send-btn" data-testid="emma-send">
          <Send size={16} />
        </button>
      </form>
    </aside>
  );
};

export default EmmaSidebar;
